import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import { Range } from 'react-date-range'
import { useSearchParams } from 'react-router-dom'

import useAccounts from 'hooks/useAccounts'
import useUpdateEffect from 'hooks/useUpdateEffect'

export interface FilterQueryConfig<T> {
    /**
     * The query parameter key for this filter (e.g., 'account_id', 'type', 'status')
     */
    queryParamKey: string
    /**
     * Currently selected values for this filter (as strings from URL)
     */
    selectedValues: T[]
    /**
     * Setter function to update selected values
     */
    setSelectedValues: Dispatch<SetStateAction<T[]>>
}

export interface FiltersWithQueryParamsNewProps<T> {
    /**
     * Configuration for dynamic filters
     */
    filters: FilterQueryConfig<T>[]
    /**
     * Date range filter
     */
    dateRange: Range | undefined
    setDateRange: Dispatch<SetStateAction<Range | undefined>>
    /**
     * Optional callback to get the full query params string
     */
    setQueryParams?: (qp: string) => void
    /**
     * Optional callback called when filters are initialized from URL on mount
     */
    onFiltersInitialized?: () => void
}

export const ACCOUNT_ID_QUERY_PARAM_KEY = 'account_id'

const isValidDate = (d: unknown): d is Date => {
    return d instanceof Date && !Number.isNaN(d.getTime())
}

const safeParseISO = (s: string | null | undefined): Date | undefined => {
    if (!s) {
        return undefined
    }
    const toData = new Date(s)
    return isValidDate(toData) ? toData : undefined
}

const normaliseRange = (start?: Date, end?: Date): { start?: Date; end?: Date } => {
    if (start && end && end < start) return { start: end, end: start }
    return { start, end }
}

const getNewAccountIdsSelection = (
    activeAccount: string,
    currentlySelectedAccountIds: string[],
    hasUserManuallyChangedAccount: boolean,
): string[] => {
    // User has manually changed the account (e.g. via the account switcher / TEST/LIVE toggle)
    // Old account filters are no longer relevant, so discard them all
    if (hasUserManuallyChangedAccount) {
        return []
    }

    // Initial load of the page when 'none' has specifically been selected (de-selected all accounts)
    if (currentlySelectedAccountIds.length === 1 && currentlySelectedAccountIds[0] === 'none') {
        return []
    }

    // Return account IDs from URL (empty array if none, or the shared filter values)
    return currentlySelectedAccountIds
}

const FiltersWithQueryParamsNew = <T,>({
    filters,
    dateRange,
    setDateRange,
    setQueryParams,
    onFiltersInitialized,
}: FiltersWithQueryParamsNewProps<T>) => {
    const [, setSearchParams] = useSearchParams()
    const { activeAccount } = useAccounts()
    const [activeAccountChangeLog, setActiveAccountChangeLog] = useState<string[]>([])

    useEffect(() => {
        // Track primary account changes to determine if a non-initial account change has occurred
        // e.g. the user used the account UI controls (TEST/LIVE switcher or account dropdown)
        // NOT PERSISTENT --> log discarded on component unmount
        if (activeAccount) {
            // Only last 2 account changes are tracked to keep array small
            setActiveAccountChangeLog((prev) => [...prev.slice(-1), activeAccount.id])
        }
    }, [activeAccount])

    const addFiltersToUrl = useCallback(
        (filters: FilterQueryConfig<T>[], dateRange: Range | undefined) => {
            const params = new URLSearchParams()

            // build filter params (properly encoded; supports repeated keys)
            filters.forEach((filter) => {
                const values = filter.selectedValues

                if (filter.queryParamKey === ACCOUNT_ID_QUERY_PARAM_KEY) {
                    if (values.length) {
                        values.forEach((v) => params.append(filter.queryParamKey, String(v)))
                    } else {
                        params.append(filter.queryParamKey, 'none')
                    }
                } else {
                    values.forEach((v) => params.append(filter.queryParamKey, String(v)))
                }
            })
            const startDate = dateRange?.startDate
            const endDate = dateRange?.endDate
            if (isValidDate(startDate)) {
                params.set('from', startDate.toISOString())
            }
            if (isValidDate(endDate)) {
                params.set('through', endDate.toISOString())
            }

            const qp = params.toString()
            setQueryParams?.(qp)
            setSearchParams(qp, { replace: true })
        },
        [setQueryParams, setSearchParams],
    )

    // Handle initial load of the page and user manually changing the account
    useEffect(() => {
        const isInitialLoad = activeAccountChangeLog.length === 1
        const params = new URLSearchParams(window.location.search)
        const from = params.get('from')
        const through = params.get('through')

        filters.forEach((filter) => {
            const values = params.getAll(filter.queryParamKey)
            if (filter.queryParamKey === ACCOUNT_ID_QUERY_PARAM_KEY) {
                const newAccountIdsSelection = getNewAccountIdsSelection(
                    activeAccountChangeLog.at(-1)!,
                    values,
                    activeAccountChangeLog.length > 1,
                )
                filter.setSelectedValues(newAccountIdsSelection as unknown as T[])
            } else {
                filter.setSelectedValues(values as unknown as T[])
            }
        })

        const start = safeParseISO(from)
        const end = safeParseISO(through)
        const { start: normStart, end: normEnd } = normaliseRange(start, end)

        if (normStart || normEnd) {
            setDateRange((prev) => ({
                ...prev,
                startDate: normStart ?? prev?.startDate,
                endDate: normEnd ?? prev?.endDate,
            }))
        }

        // Notify parent that filters have been initialized from URL
        if (isInitialLoad && onFiltersInitialized) {
            // Use setTimeout to ensure state updates have been processed
            setTimeout(() => {
                onFiltersInitialized()
            }, 0)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeAccountChangeLog])

    // Update URL when any filter or date range changes
    useUpdateEffect(() => {
        addFiltersToUrl(filters, dateRange)
    }, [
        JSON.stringify(filters.map((f) => f.selectedValues)),
        dateRange?.startDate,
        dateRange?.endDate,
        addFiltersToUrl,
    ])

    return <></>
}

export default FiltersWithQueryParamsNew
